/**
 * Asset register extensions — the register CSV export (AST-24), department / location custody
 * (AST-25), vendor and legal-entity links (AST-26 / AST-27), and the vendor detail's asset and
 * ticket lists. One router under `/asset-management/register`; it adds routes beside
 * `asset.contract.ts` rather than growing it (other work edits that file concurrently), and it
 * reuses that file's `Asset*` shapes instead of redefining them.
 *
 * Permissions, all checked again per record in the handler (scope, not just the entry gate):
 *   - export: `asset:export` AND `asset:read`; purchase-value / book-value columns only with
 *     `asset.financial:read`; rows limited to the caller's asset scope.
 *   - custody: `asset:assign` / `asset:return` against the holding department or location, so the
 *     holder's head (a department- or location-scoped grant) can manage it; reads need `asset:read`.
 *   - links: `asset:edit` on the asset; the vendor must exist in the org; the legal entity too.
 *   - vendor lists: `asset.vendor:read` plus `asset:read` (assets) / `asset:maintenance:read` (tickets).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import {
  assetConditionSchema,
  assetSchema,
  assetStatusSchema,
  custodyTypeSchema,
  maintenanceIssueSchema,
} from './asset.contract.js';
import { assetCsvExportSchema } from './vendor.contract.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

// ---------------------------------------------------------------------------
// Register export (AST-24)
// ---------------------------------------------------------------------------

export const exportRegisterQuerySchema = z.object({
  categoryId: uuidSchema.optional(),
  typeId: uuidSchema.optional(),
  status: assetStatusSchema.optional(),
  locationId: uuidSchema.optional(),
  /** The asset's effective legal entity (its own, else its location's). */
  legalEntityId: uuidSchema.optional(),
  vendorId: uuidSchema.optional(),
  /** Current custodian. */
  personId: uuidSchema.optional(),
  search: z.string().trim().max(100).optional(),
});
export type ExportRegisterQuery = z.infer<typeof exportRegisterQuerySchema>;

// ---------------------------------------------------------------------------
// Department / location custody (AST-25)
// ---------------------------------------------------------------------------

export const custodyHolderTypeSchema = z.enum(['department', 'location']);
export type CustodyHolderType = z.infer<typeof custodyHolderTypeSchema>;

/** Only the non-person custody kinds — person custody stays in `AssetAssignment`. */
export const holdCustodyTypeSchema = custodyTypeSchema.extract(['departmental', 'secondary']);
export type HoldCustodyType = z.infer<typeof holdCustodyTypeSchema>;

export const assetCustodyHoldSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assetId: uuidSchema,
  assetCode: z.string(),
  assetName: z.string(),
  holderType: custodyHolderTypeSchema,
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  custodyType: holdCustodyTypeSchema,
  startedAt: instantSchema,
  endedAt: instantSchema.nullable(),
  note: z.string().nullable(),
});
export type AssetCustodyHold = z.infer<typeof assetCustodyHoldSchema>;

export const createCustodyHoldBodySchema = z
  .object({
    holderType: custodyHolderTypeSchema,
    departmentId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
    custodyType: holdCustodyTypeSchema.default('departmental'),
    note: z.string().trim().max(500).optional(),
  })
  .refine(
    (b) =>
      (b.holderType === 'department' && b.departmentId !== undefined && b.locationId === undefined) ||
      (b.holderType === 'location' && b.locationId !== undefined && b.departmentId === undefined),
    { message: 'Provide exactly the departmentId (department) or locationId (location) of the holder' },
  );
export type CreateCustodyHoldBody = z.infer<typeof createCustodyHoldBodySchema>;

export const releaseCustodyHoldBodySchema = z.object({
  condition: assetConditionSchema,
  note: z.string().trim().max(500).optional(),
});
export type ReleaseCustodyHoldBody = z.infer<typeof releaseCustodyHoldBodySchema>;

export const listCustodyHoldsQuerySchema = paginationQuerySchema.extend({
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  /** `open` (default) = current holds only. */
  state: z.enum(['open', 'all']).default('open'),
});
export type ListCustodyHoldsQuery = z.infer<typeof listCustodyHoldsQuerySchema>;

// ---------------------------------------------------------------------------
// Vendor / legal-entity links (AST-26 / AST-27)
// ---------------------------------------------------------------------------

export const assetLinksSchema = z.object({
  assetId: uuidSchema,
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  /** What is stored on the asset; null = derive from the location. */
  legalEntityId: uuidSchema.nullable(),
  /** What reporting and scoping use: the asset's own, else its location's legal entity. */
  effectiveLegalEntityId: uuidSchema.nullable(),
  effectiveLegalEntityName: z.string().nullable(),
  legalEntitySource: z.enum(['asset', 'location']).nullable(),
});
export type AssetLinks = z.infer<typeof assetLinksSchema>;

/** `null` clears a link; omit a field to leave it unchanged. */
export const patchAssetLinksBodySchema = z
  .object({
    vendorId: uuidSchema.nullable().optional(),
    legalEntityId: uuidSchema.nullable().optional(),
  })
  .strict()
  .refine((b) => b.vendorId !== undefined || b.legalEntityId !== undefined, {
    message: 'Provide vendorId and/or legalEntityId',
  });
export type PatchAssetLinksBody = z.infer<typeof patchAssetLinksBodySchema>;

export const vendorAssetsQuerySchema = paginationQuerySchema.extend({
  cursor: uuidSchema.optional(),
});

/** A maintenance ticket as listed on a vendor's detail — `MaintenanceIssue` plus nothing extra. */
export const vendorTicketSchema = maintenanceIssueSchema;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetRegisterExtContract = c.router(
  {
    exportRegister: {
      method: 'GET',
      path: '/export',
      query: exportRegisterQuerySchema,
      summary:
        'Export the asset register as CSV: caller scope only, financial columns only with asset.financial:read (asset:export + asset:read)',
      metadata: routeMeta(['asset:export', 'asset:read']),
      responses: {
        200: assetCsvExportSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    listCustodyHolds: {
      method: 'GET',
      path: '/custody-holds',
      query: listCustodyHoldsQuerySchema,
      summary: 'List department/location custody holds in the caller’s asset scope (asset:read)',
      metadata: routeMeta('asset:read'),
      responses: {
        200: paginatedSchema(assetCustodyHoldSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    listAssetCustodyHolds: {
      method: 'GET',
      path: '/assets/:assetId/custody-holds',
      pathParams: z.object({ assetId: uuidSchema }),
      summary: "An asset's department/location custody history, newest first (asset:read)",
      metadata: routeMeta('asset:read'),
      responses: {
        200: z.array(assetCustodyHoldSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    createCustodyHold: {
      method: 'POST',
      path: '/assets/:assetId/custody-holds',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: createCustodyHoldBodySchema,
      summary: 'Place an available asset in department or location custody (asset:assign)',
      metadata: routeMeta('asset:assign'),
      responses: {
        201: assetCustodyHoldSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    releaseCustodyHold: {
      method: 'POST',
      path: '/assets/:assetId/custody-holds/release',
      pathParams: z.object({ assetId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: releaseCustodyHoldBodySchema,
      summary: 'Release the asset’s current department/location custody (asset:assign / asset:return)',
      metadata: routeMeta(['asset:assign', 'asset:return']),
      responses: {
        200: assetCustodyHoldSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    getAssetLinks: {
      method: 'GET',
      path: '/assets/:assetId/links',
      pathParams: z.object({ assetId: uuidSchema }),
      summary: "An asset's vendor and legal-entity links, with the derived legal entity (asset:read)",
      metadata: routeMeta('asset:read'),
      responses: {
        200: assetLinksSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    patchAssetLinks: {
      method: 'PATCH',
      path: '/assets/:assetId/links',
      pathParams: z.object({ assetId: uuidSchema }),
      body: patchAssetLinksBodySchema,
      summary: "Set or clear an asset's vendor and/or legal entity (asset:edit)",
      metadata: routeMeta('asset:edit'),
      responses: {
        200: assetLinksSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    listVendorAssets: {
      method: 'GET',
      path: '/vendors/:vendorId/assets',
      pathParams: z.object({ vendorId: uuidSchema }),
      query: vendorAssetsQuerySchema,
      summary: 'Assets procured from a vendor, in the caller’s asset scope (asset.vendor:read + asset:read)',
      metadata: routeMeta(['asset.vendor:read', 'asset:read']),
      responses: {
        200: paginatedSchema(assetSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    listVendorTickets: {
      method: 'GET',
      path: '/vendors/:vendorId/maintenance-tickets',
      pathParams: z.object({ vendorId: uuidSchema }),
      query: vendorAssetsQuerySchema,
      summary:
        'Maintenance tickets linked to a vendor, in the caller’s maintenance scope (asset.vendor:read + asset:maintenance:read)',
      metadata: routeMeta(['asset.vendor:read', 'asset:maintenance:read']),
      responses: {
        200: paginatedSchema(vendorTicketSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
  },
  { pathPrefix: '/asset-management/register' },
);
